import React, { forwardRef, useState, useRef } from 'react';

import {
  FileInputWrapper,
  FileInputLabel,
  HiddenInput,
  DropZone,
  UploadIcon,
  DropZoneText,
  AcceptedFormats,
  FilePreview,
  FileIcon,
  FileInfo,
  FileName,
  UploadStatus,
  SpinnerIcon,
  RemoveButton,
  ErrorText,
} from './FileUploadInput.styles';

const DEFAULT_ACCEPT = '.pdf,.jpg,.jpeg,.png';
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024; // 5MB

interface FileUploadInputProps {
  label?: string;
  name?: string;
  accept?: string;
  maxSize?: number;
  uploadUrl?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  colorLabel?: string;
  onChange?: () => void;
}

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

const FileUploadInput = forwardRef<HTMLInputElement, FileUploadInputProps>(
  ({ label, name, accept, maxSize, uploadUrl, required, error, helperText, colorLabel, onChange }, ref) => {
    const [fileName, setFileName] = useState('');
    const [uploadState, setUploadState] = useState<UploadState>('idle');
    const [clientError, setClientError] = useState('');
    const [fileKey, setFileKey] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const effectiveAccept = accept || DEFAULT_ACCEPT;
    const effectiveMaxSize = maxSize || DEFAULT_MAX_SIZE;

    const formatAcceptLabel = () => {
      return effectiveAccept.split(',').map(ext => ext.trim().replace('.', '').toUpperCase()).join(', ');
    };

    const formatMaxSizeLabel = () => {
      return `${effectiveMaxSize / (1024 * 1024)}MB`;
    };

    const validateFile = (file: File): string | null => {
      const acceptedExtensions = effectiveAccept.split(',').map(ext => ext.trim().toLowerCase());
      const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
      const isValidType = acceptedExtensions.some(ext => fileExtension === ext);

      if (!isValidType) {
        return `Formato não permitido. Aceitos: ${acceptedExtensions.join(', ')}`;
      }

      if (file.size > effectiveMaxSize) {
        return `Arquivo excede o tamanho máximo de ${formatMaxSizeLabel()}`;
      }

      return null;
    };

    const uploadFile = async (file: File) => {
      if (!uploadUrl) {
        setUploadState('error');
        return;
      }

      setUploadState('uploading');

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(uploadUrl, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.status}`);
        }

        const data = await response.json();
        const key = data.upload?.fileKey;

        if (key) {
          setFileKey(key);
          setUploadState('success');
        } else {
          throw new Error('fileKey not returned');
        }
      } catch {
        setUploadState('error');
        setFileKey('');
      }
    };

    const processFile = (file: File) => {
      setClientError('');
      setUploadState('idle');
      setFileKey('');

      const validationError = validateFile(file);
      if (validationError) {
        setClientError(validationError);
        setFileName('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        onChange?.();
        return;
      }

      setFileName(file.name);
      uploadFile(file);
      onChange?.();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        setFileName('');
        onChange?.();
        return;
      }
      processFile(file);
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (uploadState === 'uploading') return;

      const file = e.dataTransfer.files?.[0];
      if (file) processFile(file);
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      if (uploadState !== 'uploading') setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    const handleDropZoneClick = () => {
      if (uploadState !== 'uploading') {
        fileInputRef.current?.click();
      }
    };

    const handleRemove = (e: React.MouseEvent) => {
      e.stopPropagation();
      setFileName('');
      setFileKey('');
      setUploadState('idle');
      setClientError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onChange?.();
    };

    const setRefs = (el: HTMLInputElement | null) => {
      fileInputRef.current = el;
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
      }
    };

    const displayError = clientError || (error ? helperText : '');
    const isUploading = uploadState === 'uploading';
    const hasFile = !!fileName;

    return (
      <FileInputWrapper>
        {label && (
          <FileInputLabel colorLabel={colorLabel}>
            {label}
            {required && ' *'}
          </FileInputLabel>
        )}

        <HiddenInput
          id={name}
          type="file"
          ref={setRefs}
          accept={effectiveAccept}
          onChange={handleFileChange}
        />

        {!hasFile ? (
          <DropZone
            onClick={handleDropZoneClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            hasError={!!displayError}
            isDragging={isDragging}
            disabled={isUploading}
          >
            <UploadIcon>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </UploadIcon>
            <DropZoneText>
              Arraste um arquivo ou <strong>clique para selecionar</strong>
            </DropZoneText>
            <AcceptedFormats>
              {formatAcceptLabel()} • máx. {formatMaxSizeLabel()}
            </AcceptedFormats>
          </DropZone>
        ) : (
          <FilePreview>
            <FileIcon>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </FileIcon>
            <FileInfo>
              <FileName>{fileName}</FileName>
              {uploadState !== 'idle' && (
                <UploadStatus status={uploadState}>
                  {uploadState === 'uploading' && (
                    <SpinnerIcon>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                    </SpinnerIcon>
                  )}
                  {uploadState === 'uploading' && 'Enviando...'}
                  {uploadState === 'success' && '✓ Enviado'}
                  {uploadState === 'error' && 'Erro ao enviar. Tente novamente.'}
                </UploadStatus>
              )}
            </FileInfo>
            {!isUploading && (
              <RemoveButton type="button" onClick={handleRemove} title="Remover arquivo">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </RemoveButton>
            )}
          </FilePreview>
        )}

        {displayError && <ErrorText>{displayError}</ErrorText>}
        <input type="hidden" name={name} value={fileKey} />
      </FileInputWrapper>
    );
  }
);

export default FileUploadInput;
